import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Form, Input, Select, Row, Col, Button, DatePicker, Alert } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { formatLabel, selectFieldRules } from "../../utils";
import dayjs from "dayjs";
import {
  useUpdateCommissioningMutation,
  useGetServicesByPoIdQuery,
} from "../../store/api/serviceApi";
import { useGetEntityFilesQuery } from "../../store/api/fileApi";
import { useToast } from "../../hooks/useToast";
import S3FileUpload, { S3FileUploadRef } from "./S3FileUpload";
import UploadedFilesList from "./UploadedFilesList";
import type { ServiceResponse } from "@OrianaTypes";

const COMMISSIONING_ENTITY_TYPE = "commissioning";

interface CommissioningFormModalProps {
  visible: boolean;
  onClose: () => void;
  poId: string;
  editData?: ServiceResponse | null;
}

const CommissioningFormModal: React.FC<CommissioningFormModalProps> = ({
  visible,
  onClose,
  poId,
  editData = null,
}) => {
  const [form] = Form.useForm();
  const toast = useToast();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const fileUploadRef = useRef<S3FileUploadRef>(null);

  // Get services to find eligible ones for commissioning
  const { data: servicesData = [] } = useGetServicesByPoIdQuery(poId, {
    skip: !visible || !poId,
  });

  const [updateCommissioning, { isLoading: isUpdating }] = useUpdateCommissioningMutation();

  const serviceIdForFiles = editData?.serviceId != null ? String(editData.serviceId) : "";
  const { data: commissioningFilesRes } = useGetEntityFilesQuery(
    { entityType: COMMISSIONING_ENTITY_TYPE, entityId: serviceIdForFiles },
    { skip: !serviceIdForFiles || !visible }
  );
  const commissioningFiles = commissioningFilesRes?.data ?? [];

  // Status options - using lowercase to match backend
  const statusOptions = [
    { value: "done", label: "Done" },
    { value: "pending", label: "Pending" },
    { value: "hold", label: "Hold" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // Eligible services for commissioning (pre-commissioning status === 'done')
  const eligibleServices = useMemo(() => {
    return servicesData.filter((s) => s.preCommissioningStatus?.toLowerCase() === "done");
  }, [servicesData]);

  useEffect(() => {
    if (visible && editData) {
      form.setFieldsValue({
        commissioningEcdFromClient: editData.commissioningEcdFromClient
          ? dayjs(editData.commissioningEcdFromClient)
          : null,
        commissioningServiceTicketNo: editData.commissioningServiceTicketNo || "",
        commissioningIssues: editData.commissioningIssues || "",
        commissioningSolution: editData.commissioningSolution || "",
        commissioningDate: editData.commissioningDate
          ? dayjs(editData.commissioningDate)
          : null,
        commissioningStatus: editData.commissioningStatus || "",
        commissioningRemarks: editData.commissioningRemarks || "",
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, editData, form]);

  const handleSubmit = async () => {
    if (!editData) {
      toast.error("Please select a service to update");
      return;
    }

    try {
      const values = await form.validateFields();

      await updateCommissioning({
        id: editData.serviceId,
        data: {
          commissioningEcdFromClient: values.commissioningEcdFromClient?.format("YYYY-MM-DD") || undefined,
          commissioningServiceTicketNo: values.commissioningServiceTicketNo || undefined,
          commissioningIssues: values.commissioningIssues || undefined,
          commissioningSolution: values.commissioningSolution || undefined,
          commissioningDate: values.commissioningDate?.format("YYYY-MM-DD") || undefined,
          commissioningStatus: values.commissioningStatus || undefined,
          commissioningRemarks: values.commissioningRemarks || undefined,
        },
        poId,
      }).unwrap();
      toast.success("Commissioning updated successfully");
      handleCancel();
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to update commissioning details");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  if (!editData) {
    return (
      <Modal
        title="Update Commissioning"
        open={visible}
        onCancel={handleCancel}
        width={900}
        destroyOnClose
        footer={[
          <Button key="close" onClick={handleCancel}>
            Close
          </Button>,
        ]}
      >
        <Alert
          type="info"
          message="No Service Selected"
          description="Please select a service from the table to update commissioning details."
          showIcon
        />
      </Modal>
    );
  }

  return (
    <Modal
      title={`Update Commissioning - Service #${editData.serviceId}`}
      open={visible}
      onCancel={handleCancel}
      width={900}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isUpdating}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isUpdating}
          style={{ backgroundColor: "#71a241" }}
        >
          Update
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        style={{ marginTop: "1rem" }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Service ID">
              <Input value={`#${editData.serviceId}`} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Serial Number">
              <Input value={editData.serialNumber} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="commissioningEcdFromClient" label="ECD From Client">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder="Expected commissioning date from client" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="commissioningServiceTicketNo" label="Service Ticket No">
              <Input placeholder="Service ticket number" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="commissioningDate" label="Commissioning Date">
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="commissioningStatus"
              label="Commissioning Status"
              rules={selectFieldRules}
            >
              <Select placeholder="Select status" options={statusOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="commissioningIssues" label="Issues">
              <Input.TextArea placeholder="Issues faced" rows={2} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="commissioningSolution" label="Solution">
              <Input.TextArea placeholder="Solution provided" rows={2} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item name="commissioningRemarks" label="Remarks">
              <Input.TextArea placeholder="Enter remarks" rows={2} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label="Upload Document (Commissioning)"
              tooltip="Upload up to 2 documents. Supported formats: Images, PDF, Word, Excel"
            >
              <S3FileUpload
                ref={fileUploadRef}
                fileList={fileList}
                onChange={setFileList}
                minFiles={0}
                maxFiles={2}
                maxSizeMB={10}
                label=""
                buttonLabel="Click to Upload"
                helperText="Supported: Images (JPG, PNG, GIF, SVG), PDF, Word, Excel."
                poId={poId}
                entityType={COMMISSIONING_ENTITY_TYPE}
                entityId={serviceIdForFiles || undefined}
              />
              <UploadedFilesList
                files={commissioningFiles}
                title="Commissioning Documents"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CommissioningFormModal;
